<template>
  <div class="row">
    <div class="col-12">
      <div class="tile">
        <div class="form-inline">
          <form-select :model="filterFormModel" attribute="group" class="mr-1" @input="load" :options="[
                    {value: 'day', text: $t('byDay')},
                    {value: 'month', text: $t('byMonth')},
                    {value: 'quarter', text: $t('byQuarter')},
                    ]">
          </form-select>
          <client-select :model="filterFormModel" attribute="client" @input="load" class="mr-1"></client-select>
          <time-range-picker ref="timeRangePicker" :model="filterFormModel" @input="load" :label="$t('time')"
                             :default-shortcut="3" class="mr-1"></time-range-picker>
          <button @click="load" class="btn btn-primary mr-1">{{ $t('search') }}</button>
          <button @click="reset" class="btn btn-secondary mr-1">Reset</button>
        </div>
      </div>
      <div v-if="data.length === 0" class="tile text-center text-danger">{{ $t('noData') }}</div>
      <div v-else>
        <div class="tile">
          <div class="row">
            <div class="col">
              <div>
                <apexchart :height="350" :options="chartOptions" :series="chartData"></apexchart>
              </div>
            </div>
          </div>
        </div>
        <div class="tile">
          <div class="table-responsive c-template-table">
            <table class="table table-hover table-bordered">
              <thead>
              <tr>
                <th></th>
                <th></th>
                <th class="text-center">{{ $t('total') }}</th>
                <th class="text-center" v-for="(item, index) in data" :key="index">{{ item.name }}</th>
              </tr>
              </thead>
              <tbody>
              <tr class="bg-success">
                <td></td>
                <td><b class="text-uppercase">{{ $t('totals') }}</b></td>
                <td class="text-center" :class="getTextColor(getTotalSum())">{{ getTotalSum() | numFormat }}</td>
                <td class="text-center" :class="getTextColor(getColumnSum(index))" v-for="(item, index) in data"
                    :key="index">
                  {{ getColumnSum(index) | numFormat }}
                </td>
              </tr>
              <tr class="bg-warning">
                <td></td>
                <td><b class="text-uppercase">{{ $t('average') }}</b></td>
                <td class="text-center" :class="getTextColor(getTotalAverage())">{{
                    getTotalAverage() | numFormat
                  }}
                </td>
                <td class="text-center" :class="getTextColor(getColumnAverage(index))" v-for="(item, index) in data"
                    :key="index">
                  {{ getColumnAverage(index) | numFormat }}
                </td>
              </tr>
              <tr v-for="(rowData, rowIndex) in getMaxDataLength()" :key="rowIndex">
                <td class="text-center">{{ rowIndex + 1 }}</td>
                <td><b>{{ getXValue(rowIndex) }}</b></td>
                <td class="text-center" :class="getTextColor(getTotalYValue(rowIndex))">
                  {{ getTotalYValue(rowIndex) | numFormat }}
                </td>
                <td class="text-center" :class="getTextColor(getYValue(item, rowIndex))" v-for="(item, index) in data"
                    :key="index">
                  {{ getYValue(item, rowIndex) | numFormat }}
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TimeRangePicker from "@/components/TimeRangePicker";
import moment from 'moment';
import VueApexCharts from 'vue-apexcharts';
import ClientSelect from "@/components/ClientSelect.vue";

export default {
  components: {ClientSelect, TimeRangePicker, 'apexchart': VueApexCharts},
  data: function () {
    return {
      filterFormModel: {
        group: 'day',
        client: null,
      },
      loading: true,
      data: [],
      chartOptions: {
        xaxis: {},
        chart: {
          zoom: {
            enabled: false
          },
          type: 'line'
        }
      },
      chartData: [],
    }
  },
  methods: {
    generateRandomColor() {
      const letters = '0123456789ABCDEF';
      let color = '#';
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    },
    load() {
      this.data = []
      let loader = this.$loading.show();
      this.loading = true;
      if (!this.filterFormModel.to || !this.filterFormModel.from) {
        this.filterFormModel.from = moment().startOf('month').format('x');
        this.filterFormModel.to = moment().endOf('month').format('x');
      }
      this.$service.get('/report-usage/count', {
        params: this.filterFormModel
      }).then(response => {
        this.data = response.data;

        const baseColors = ['#FF4560', '#008FFB', '#00E396', '#FEB019', '#775DD0', '#546E7A', '#26A69A', '#D10CE8'];
        const colors = this.data.map((_, index) => {
          if (index < baseColors.length) {
            return baseColors[index];
          } else {
            return this.generateRandomColor();
          }
        });
        this.chartOptions = {
          colors: colors,
          chart: {
            type: 'line',
            zoom: {
              enabled: false
            }
          },
          stroke: {
            curve: 'straight'
          },
          markers: {
            size: 1
          },
          yaxis: {
            labels: {
              formatter: function (value) {
                return new Intl.NumberFormat().format(value)
              }
            }
          }
        }
        this.chartData = this.data
      }).finally(() => {
        loader.hide();
        this.loading = false;
      });
    },
    reset() {
      this.filterFormModel = {
        group: 'day',
        client: null,
      };
      this.load()
    },
    getTotalSum() {
      let sum = 0
      this.data.forEach(item => {
        item.data.forEach(row => {
          sum += row.y
        })
      })
      return sum
    },
    getColumnSum(columnIndex) {
      let sum = 0
      this.data[columnIndex].data.forEach(item => {
        sum += item.y
      })
      return sum
    },
    getTotalAverage() {
      const totalCount = this.data[0].data.length
      return totalCount === 0 ? 0 : this.getTotalSum() / totalCount
    },
    getColumnAverage(columnIndex) {
      const columnCount = this.data[0].data.length
      return columnCount === 0 ? 0 : this.getColumnSum(columnIndex) / columnCount
    },
    getMaxDataLength() {
      return Math.max(...this.data.map(item => item.data.length))
    },
    getXValue(rowIndex) {
      return this.data[0].data[rowIndex] ? this.data[0].data[rowIndex].x : ''
    },
    getTotalYValue(rowIndex) {
      return this.data.reduce((total, current) => {
        return total + (current.data[rowIndex] ? current.data[rowIndex].y : 0)
      }, 0)
    },
    getYValue(employee, rowIndex) {
      return employee.data[rowIndex] ? employee.data[rowIndex].y : ''
    },
    getTextColor(val) {
      if (val) return ''
      return 'text-secondary'
    },
  },
  mounted() {
    this.load();
  },
  i18n: {
    messages: {
      en: {
        byDay: 'By day',
        byMonth: 'By month',
        byQuarter: 'By quarter',
        totals: 'Total',
        noData: 'No data!',
      },
      vi: {
        byDay: 'Theo ngày',
        byMonth: 'Theo tháng',
        byQuarter: 'Theo quý',
        totals: 'Tổng',
        noData: 'Không có dữ liệu!',
      }
    }
  }
}
</script>